import { useAsync } from 'react-use';
import * as Sentry from '@sentry/react';

import api from '../api/axios';
import authorizationHeader from '../api/token';

// GET learning units
export function useGetLearnunits() {
  const { value, loading, error } = useAsync(() => getLearnunits());
  return [value, loading, error];
}

export const getLearnunits = async () => {
  const token = await authorizationHeader();
  const learningUnits = await api
    .get(`/learnunits/`, {
      headers: token,
    })
    .then(res => res)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      return err;
    });
  return learningUnits;
};

// GET learning units paginated
export function useGetPaginatedLearnunits(page, limit, searchFilter) {
  const { value, loading, error } = useAsync(
    () => getPaginatedLearnunits(page, limit, searchFilter),
    [page, limit, searchFilter]
  );
  return [value, loading, error];
}

export const getPaginatedLearnunits = async (page, limit, searchFilter = '') => {
  const token = await authorizationHeader();
  const learningUnits = await api
    .get(
      `/learnunits/paginated?page=${page}&limit=${limit}&searchFilter=${encodeURIComponent(
        searchFilter
      )}`,
      {
        headers: token,
      }
    )
    .then(res => res)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      return err;
    });
  return learningUnits.data;
};

// Errores de validación esperados del backend que no se reportan a Sentry
const EXPECTED_ERROR_STATUSES = [400, 404, 409];

const handleLearnunitError = err => {
  if (!EXPECTED_ERROR_STATUSES.includes(err?.response?.status)) {
    Sentry.captureException(err);
    if (err?.response?.data) {
      Sentry.captureMessage(err.response.data);
    }
  }
  return err;
};

// Extrae el mensaje de error enviado por el backend (texto o { message })
export const getLearnunitErrorMessage = (response, fallback) => {
  const data = response?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data?.message) return data.message;
  return fallback;
};

// Post new Learnunit (si ya existe una equivalente, el backend la devuelve sin crear otra)
export const postLearnunit = async ua => {
  const token = await authorizationHeader();
  const response = await api
    .post(`/learnunits/new`, ua, {
      headers: token,
    })
    .then(res => res)
    .catch(handleLearnunitError);
  return response;
};

// PUT learnunit name
export const putLearnunit = async (id, data) => {
  const token = await authorizationHeader();
  const response = await api
    .put(`/learnunits/${id}`, data, {
      headers: token,
    })
    .then(res => res)
    .catch(handleLearnunitError);
  return response;
};

// DELETE learnunit (solo si no tiene asociaciones)
export const deleteLearnunit = async id => {
  const token = await authorizationHeader();
  const response = await api
    .delete(`/learnunits/delete/${id}`, {
      headers: token,
    })
    .then(res => res)
    .catch(handleLearnunitError);
  return response;
};

// POST merge: fusiona duplicateId en targetId y elimina duplicateId
export const mergeLearnunits = async (targetId, duplicateId) => {
  const token = await authorizationHeader();
  const response = await api
    .post(`/learnunits/clean/${targetId}/${duplicateId}`, null, {
      headers: token,
    })
    .then(res => res)
    .catch(handleLearnunitError);
  return response;
};

// GET learning unit by course_id
export function useGetCourseLearningUnits(course_id) {
  const { value, loading, error } = useAsync(() => getCourseLearningUnits(course_id), [course_id]);
  return [value, loading, error];
}

const getCourseLearningUnits = async course_id => {
  const token = await authorizationHeader();
  const learningUnits = await api
    .get(`course-learnunits/${course_id}`, {
      headers: token,
    })
    .then(res => res)
    .catch(err => {
      if (err?.response?.status !== 404) {
        Sentry.captureException(err);
        if (err?.response?.data) {
          Sentry.captureMessage(err.response.data);
        }
      }
      return err;
    });
  return learningUnits;
};

export const patchCourseLearnunit = async (id, data) => {
  const token = await authorizationHeader();
  const response = await api.patch(`course-learnunits/${id}`, data, {
    headers: token,
  });
  return response;
};

export const deleteCourseLearunit = async id => {
  const token = await authorizationHeader();
  const learningUnits = await api
    .delete(`course-learnunits/${id}`, {
      headers: token,
    })
    .then(res => res)
    .catch(err => {
      if (err?.response?.status !== 400) {
        Sentry.captureException(err);
        if (err?.response?.data) {
          Sentry.captureMessage(err.response.data);
        }
      }
      return err;
    });
  return learningUnits;
};
